import { inject } from "@angular/core";
import { EventType, NavigationEnd, Router } from "@angular/router";
import { Logger } from "@timeghost/web-api";
import { filter } from "rxjs";
import { createRxValue, takeUntilDestroyed } from "./utils";

type RouteMatchFn = (s: string) => boolean;
type RouteMatcherValue = string | RegExp | RouteMatchFn;
type RouteMatcher = { [key: string]: RouteMatcherValue };
const log = new Logger("useRouteMatcher");
export function useRouteMatcher<T extends RouteMatcher>(matcher: T) {
	const router = inject(Router);
	const matcherMap = Object.entries(matcher).reduce(
		(acc, [key, m]) => {
			if (typeof m === "string") acc.simple.push([key, m]);
			if (m instanceof RegExp) acc.regex.push([key, m]);
			if (typeof m === "function") acc.method.push([key, m]);
			return acc;
		},
		{ regex: [], simple: [], method: [] } as {
			regex: [string, RegExp][];
			simple: [string, string][];
			method: [string, RouteMatchFn][];
		}
	);
	const matchUrl = (pathname: string) => {
		if (!pathname) return null;
		const simpleUrlMatch = matcherMap.simple.find(([, m]) => m === pathname);
		if (simpleUrlMatch) return simpleUrlMatch[0];
		const regexUrlMatch = matcherMap.regex.find(([, m]) => m.test(pathname));
		if (regexUrlMatch) return regexUrlMatch[0];
		const methodUrlMatch = matcherMap.method.find(([, m]) => m(pathname));
		if (methodUrlMatch) return methodUrlMatch[0];
		return null;
	};
	const initialNavigationUrl = router.routerState?.snapshot?.url;
	const state = createRxValue<keyof T | null>(
		initialNavigationUrl ? matchUrl(new URL(initialNavigationUrl, location.origin).pathname) : null
	);
	log.debug("InitialValue", state.value);

	router.events
		.pipe(
			filter((ev) => ev.type === EventType.NavigationEnd),
			takeUntilDestroyed()
		)
		.subscribe((navigationEnd: NavigationEnd) => {
			const url = new URL(navigationEnd.url, location.origin).pathname;
			const pageName = matchUrl(url);
			if (pageName && pageName !== state.value) state.update(pageName);
			log.debug("NavigationEnd", pageName);
		});

	return state;
}
